'use client'

import { useEffect, useState } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { PricingCards } from '@/components/pricing/pricing-cards'
import { useOrganization } from '@clerk/nextjs'
import { hasTrialExpired } from '@/lib/plan-manager/pricing-plans'
import useUserStore from '@/lib/hooks/zustand-stores/use-user-store'
import { getOrganizationWithSubscription } from '@/app/actions/organizations'
import { usePathname, useRouter } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { IconUsers } from '@/components/ui/icons'

export default function SubscriptionRequiredDialog() {
  const [shouldShow, setShouldShow] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [isInsufficientSeats, setIsInsufficientSeats] = useState(false)
  const [currentMemberCount, setCurrentMemberCount] = useState(0)
  const [purchasedSeats, setPurchasedSeats] = useState(0)
  const { organization, isLoaded, membership } = useOrganization()
  const user = useUserStore(state => state.user)
  const pathname = usePathname()
  const router = useRouter()
  const [orgData, setOrgData] = useState<any>(null)

  // Don't show on organization management pages, pricing page, or profile pages
  const isExcludedPage = pathname?.startsWith('/organization/') || 
                         pathname?.startsWith('/organizations') ||
                         pathname?.startsWith('/organization-join') ||
                         pathname?.startsWith('/profile') ||
                         pathname === '/pricing'

  // Check if user is admin
  const isAdmin = membership?.role === 'org:admin'

  useEffect(() => {
    if (!isLoaded || !organization?.id || isExcludedPage) return

    const checkSubscription = async () => {
      try {
        const data = await getOrganizationWithSubscription(organization.id)
        setOrgData(data)
        const hasActiveSubscription = data?.subscription_details?.is_active === true
        const isInTrialPeriod = data?.created_at && !hasTrialExpired(data.created_at)
        
        // Get current member count and purchased seats
        const memberCount = organization.membersCount || 0
        const maxSeats = data?.subscription_details?.maxUsersAllowedInOrg || 0
        
        // Check if we have insufficient seats (active subscription but not enough seats)
        const hasInsufficientSeats = hasActiveSubscription && maxSeats > 0 && memberCount > maxSeats

        setCurrentMemberCount(memberCount)
        setPurchasedSeats(maxSeats)
        setIsInsufficientSeats(hasInsufficientSeats)

        // Show dialog if:
        // 1. No active subscription AND trial expired, OR
        // 2. Active subscription but insufficient seats
        const noActiveSubscriptionAndTrialExpired = !hasActiveSubscription && !isInTrialPeriod
        const shouldShowDialog = noActiveSubscriptionAndTrialExpired || hasInsufficientSeats

        setShouldShow(shouldShowDialog)
        setIsOpen(shouldShowDialog)
      } catch (error) {
        console.error('Error fetching subscription details:', error)
        setShouldShow(true)
        setIsOpen(true)
      }
    }

    checkSubscription()
  }, [isLoaded, organization?.id, isExcludedPage])

  // Reopen dialog on navigation
  useEffect(() => {
    if (shouldShow && !isExcludedPage) {
      setIsOpen(true)
    }
  }, [pathname, shouldShow, isExcludedPage])

  if (!isLoaded || !organization || !shouldShow) return null

  const handleManageMembers = () => {
    setIsOpen(false)
    router.push(`/organization/${organization.slug}`)
  }

  return (
    <Dialog 
      open={isOpen} 
      onOpenChange={(open) => {
        setIsOpen(open)
      }}
    >
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isInsufficientSeats ? 'Additional Seats Required' : 'Subscription Required'}</DialogTitle>
          <DialogDescription>
            {isInsufficientSeats ? (
              <>
                Your organization has {currentMemberCount} members but only {purchasedSeats} purchased seats.
                {isAdmin ? (
                  <> You can either purchase {currentMemberCount - purchasedSeats} additional seats or remove excess members to match your current seat limit.</>
                ) : (
                  <> Please contact your organization administrator to purchase additional seats or remove excess members.</>
                )}
              </>
            ) : (
              <>
                {isAdmin ? (
                  <>Your trial has expired. You can manage your organization members before choosing a plan, 
                  or select a plan below to continue using all features.</>
                ) : (
                  <>Your organization's trial has expired. Please contact your organization administrator to subscribe to a plan.</>
                )}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {isAdmin && (
          <div className="flex justify-center mb-6">
            <Button
              variant="outline"
              onClick={handleManageMembers}
              className="gap-2"
            >
              <IconUsers className="h-4 w-4" />
              Manage Organization Members
            </Button>
          </div>
        )}

        <div className="py-6 border-t">
          <PricingCards 
            showCheckout={true}
            user={user}
            organization={organization && orgData ? {
              id: organization.id,
              name: organization.name,
              membersCount: organization.membersCount,
              subscription_details: {
                subscription_id: orgData.subscription_details?.subscription_id ?? null,
                product_id: orgData.subscription_details?.product_id ?? '',
                is_active: orgData.subscription_details?.is_active ?? false,
                createdAt: orgData.created_at,
                maxUsersAllowedInOrg: orgData.subscription_details?.maxUsersAllowedInOrg
              }
            } : null}
            isLanding={false}
            orgId={organization?.id ?? null}
            isAdmin={isAdmin}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
} 